import { debug, UA, URI, version as JSSIPVersion, WebSocketInterface } from '@wui/jssip'
import { RTCSession } from '@wui/jssip/lib/RTCSession'
import { IncomingRequest } from '@wui/jssip/lib/SIPMessage'
import { Subscriber } from '@wui/jssip/lib/Subscriber'
import { RTCSessionEvent } from '@wui/jssip/lib/UA'
import { computed, readonly, ref, shallowRef, triggerRef } from 'vue'

import { version } from '@/../package.json'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useCallsStore } from '@/entities/calls'
import { useStoreSettingsForwarding } from '@/entities/forwarding'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { useStorePickupCall } from '@/entities/pickup-call'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { useToggle } from '@/shared/composables/use-toggle'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { Room } from '@/shared/types/store'
import { isStageOrDevelopment, tauriMode } from '@/shared/utils/import-meta'
import { clearSessionInvitedByName, getAnswerOptions, getInvitedByName, parseCallMediaRequest, setSessionInvitedByName } from '@/shared/utils/jssip'

import { call } from './call'
import { SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS } from './constants'
import { eventBus } from './event-bus'
import { onNewRTCSession } from './on-new-rtc-session'
import { onRegistrationFailed } from './on-registration-failed'
import { fireReplaceableCallReceiveEvent } from './on-replaceable-call-receive'
import { register } from './register'
import { start } from './start'
import { stop } from './stop'
import { SetupUA } from './types'
import { userStatusEndedHandler } from './use-session-event-listener'
import { RTCSessionFacade, STATE, STATUS_CODES, useRTCSessionFacade } from './use-session-facade'

if (isStageOrDevelopment()) {
  // debug.enable('JsSIP:*')
  debug.disable()
} else {
  debug.disable()
}

let ua: UA
let confUri: URI | undefined
let callingYourself = false

const {
  enable: setRegistered,
  disable: setUnRegistered,
  value: registered,
} = useToggle(false)

const {
  enable: setConnected,
  disable: setDisconnected,
} = useToggle(false)

const { checkAutoAnswer } = useAutoAnswer()

const sessions = shallowRef<Map<string, RTCSessionFacade>>(new Map())
const ringingSessions = computed(() => {
  return Array.from(sessions.value.values()).filter(({ sessionState }) => sessionState.value === STATE.RINGING)
})
const activeSessionId = ref<string>()
const registrationId = ref<string>()

export const getUA = (): UA | undefined => {
  return ua
}

export const isRegistered = (): boolean => {
  return registered.value
}

const removeSession = (id: string) => {
  if (id === activeSessionId.value) {
    activeSessionId.value = undefined
  }

  if (sessions.value.has(id)) {
    if (sessions.value.size === 1) {
      sessions.value.forEach((session) => {
        const storeMediaDevices = useStoreMediaDevices()
        const audioStream = session.localAudioStream.value
        const videoStream = session.localVideoStream.value
        const localScreenStream = session.localScreenStream.value
        if (audioStream) {
          storeMediaDevices.closeStream(audioStream)
        }
        if (videoStream) {
          storeMediaDevices.closeStream(videoStream)
        }
        if (localScreenStream) {
          storeMediaDevices.closeStream(localScreenStream)
        }
      })

      userStatusEndedHandler(id)
    }

    sessions.value.delete(id)
    useCallSessionRoomStore().unbindSession(id)
    clearSessionInvitedByName(id)

    const sessionsArray = [...sessions.value.values()]
    const connectedSessions = sessionsArray.filter(s => s.sessionState.value === STATE.CONNECTED)
    // Если по завершении сессии есть сессии в дозвоне, то показываем окно вызова у первой
    if (sessions.value.size > 0 && connectedSessions.length === 0) {
      const ringing = sessionsArray.find(s => s.sessionState.value === STATE.RINGING)
      if (ringing) {
        ringing.dialog.showDialog()
      }
    }

    triggerRef(sessions)
  }
}

const addSession = (e: RTCSessionEvent) => {
  const { currentUser } = useCurrentUser()
  const sessionId = e.session.id
  const request = e.request as IncomingRequest & { replaces?: { call_id?: string }, extraHeaders?: Array<string>, call_id: string }
  callingYourself = e.originator === 'local' ? request.to.uri.user === currentUser.value?.internalNumber : request.from.uri.user === currentUser.value?.internalNumber
  if (e.originator === 'local') {
    setActiveSession(sessionId)
  }
  const sessionFacade = useRTCSessionFacade(e.session, request)
  if (e.originator === 'remote') {
    setSessionInvitedByName(sessionId, getInvitedByName(request))
  }
  const replaceCallId = request?.replaces?.call_id
  const extraHeader = request?.extraHeaders?.at(0) as string
  sessionFacade.referToSessionId.value = extraHeader?.includes('refer-call') ? extraHeader.replace(/"/gm, '').replace(/^refer-call:/gm, '') : null
  sessions.value.set(sessionId, sessionFacade)

  const { getSettingByKey } = useStoreEnvSettings()
  const autoAnswerVideoEnabled = getSettingByKey('autoAnswer')

  const sessionRoom = useCallSessionRoomStore().getRoom(sessionId)
  const roompServedUser = sessionRoom?.pServedUser
  const isToCheck = autoAnswerVideoEnabled
    ? { audio: true, video: true }
    : checkAutoAnswer(roompServedUser)

  const autoAnswer = (e.originator === 'remote' && roompServedUser) ? isToCheck : undefined

  if (replaceCallId) {
    fireReplaceableCallReceiveEvent(replaceCallId)
  } else if (autoAnswer) {
    const { audio, video } = autoAnswer
    getAnswerOptions(audio, video, !!sessionRoom?.isGroup).then((options) => {
      sessionFacade.answer(options)
    })
  } else if (callingYourself) {
    if (e.originator === 'local') {
      // Исходящий самому себе
      sessionFacade.dialog.showDialog()
    } else if (e.originator === 'remote') {
      if (localStorage.getItem('callingYourself') === 'true') {
        // Вызов самому себе входящий
        removeSession(sessionId)
        sessionFacade.terminate()
        localStorage.removeItem('callingYourself')
      } else {
        // Вызов самому себе на другом устройстве
        sessionFacade.dialog.showDialog()
      }
    }
  } else {
    if (!sessionFacade.replaceOnProgress.value && e.originator === 'remote' && sessions.value.size > 1 && getCurrentSession()) {
      forwardHandler(sessionFacade)
    } else if (!sessionFacade.referToSessionId.value) {
      sessionFacade.dialog.showDialog()
    }
  }

  if (request.call_id) {
    sessionFacade.callId.value = request.call_id

    if (request.hasHeader('X-Satel-Params') && sessionRoom?.isGroup) {
      try {
        const r = parseCallMediaRequest(request.getHeader('X-Satel-Params'))
        const { setCallMediaRequest } = useCallsStore()
        setCallMediaRequest(request.call_id, r)
      } catch (error) {
        console.error('Failed to store call media request from X-Satel-Params', error)
      }
    }
  }

  triggerRef(sessions)
}

/*
  Получение текущего RTCSession
*/
const getCurrentSession = (): RTCSession | undefined => {
  if (!activeSessionId.value) return undefined
  const session = sessions.value.get(activeSessionId.value)
  return session?.session
}

/*
  Получение текущего RTCSessionFacade. undefined, если нет активной сессии
*/
const currentRTCSessionFacade = computed(() => {
  if (!activeSessionId.value) return undefined
  return sessions.value.get(activeSessionId.value)
})

/*
  Получение RTCSessionFacade по Session id
*/
const getRTCSessionFacadeById = (id: string): RTCSessionFacade | undefined => {
  return sessions.value.get(id)
}

const getReferRTCSessionFacade = (sessionId: string) => {
  for (const [, sessionFacade] of sessions.value) {
    if (sessionFacade.referToSessionId.value === sessionId) {
      return sessionFacade
    }
  }
  return null
}

const setActiveSession = (sessionId: string) => {
  activeSessionId.value = sessionId
  holdAllExcept(sessionId)
}

const holdAllExcept = (sessionId: string) => {
  for (const session of sessions.value.values()) {
    if (session.sessionId !== sessionId && session.sessionState.value === STATE.CONNECTED) {
      const sessionRoom = useCallSessionRoomStore().getRoom(session.sessionId)
      if (sessionRoom?.isGroup) {
        session.onHoldInConference()
      } else {
        session.toggleHold()
      }
    }
  }
}

const forwardHandler = (sessionFacade: RTCSessionFacade) => {
  const storeSettingsForwarding = useStoreSettingsForwarding()
  const rule = storeSettingsForwarding.maxPriorityCallForwardingOnBusy
  const incomingNumber = sessionFacade.number
  if (rule) {
    const regex = new RegExp(rule.aNumber, 'gm')
    const result = incomingNumber.match(regex)
    if (result && result.length) {
      sessionFacade.terminate({ status_code: STATUS_CODES.BUSY_HERE })
    }
  }
}

const linkRoomToCallSessions = (room: Room) => {
  useCallSessionRoomStore().linkRoomToSessions(room)
}

const updateRoomInSessionIfNeed = (room: Room) => {
  useCallSessionRoomStore().linkRoomToSessions(room)
  triggerRef(sessions)
}

const userAgentNameHandler = () => {
  if (tauriMode()) {
    return `WebClientRTU-tauri: ${version}, JSSIP: ${JSSIPVersion}`
  } else if (window.matchMedia('(display-mode: standalone)').matches) {
    return `WebClientRTU-app: ${version}, JSSIP: ${JSSIPVersion}`
  } else {
    return `WebClientRTU-browser: ${version}, JSSIP: ${JSSIPVersion}`
  }
}

eventBus.on('removeSession', removeSession)
eventBus.on('addSession', addSession)
eventBus.on('confirmed', (sessionId) => {
  setActiveSession(sessionId)
})
eventBus.on('unhold', (sessionId) => {
  setActiveSession(sessionId)
})

export const NO_ANSWER_TIMEOUT = 60

export function useWebRTC () {
  const init = (conf: SetupUA) => {
    confUri = URI.parse(conf.ua.uri)
    const socket = new WebSocketInterface(conf.sipWs)
    const isGuest = conf.ua?.guestToken
    ua = new UA({
      ...conf.ua,
      no_answer_timeout: NO_ANSWER_TIMEOUT,
      sockets: [socket],
      register: true,
      register_expires: 120,
      realm: 'debian',
      user_agent: userAgentNameHandler(),
    })
    if (isGuest) {
      ua.registrator().setExtraHeaders(['X-Satel-Auth-Token: ' + conf.ua.guestToken])
    }
    ua.addListener('registered', (e) => {
      const { response: { headers }} = e
      const callId = headers['Call-ID']
      if (callId && callId[0] && callId[0].parsed) {
        registrationId.value = callId[0].parsed
      }
      setRegistered()
    })
    ua.addListener('unregistered', setUnRegistered)
    ua.addListener('registrationFailed', onRegistrationFailed)
    ua.addListener('connected', () => {
      if (!isGuest) {
        useStorePickupCall().fetchAndSubscribePickupCalls()
      }
      setConnected()
    })
    ua.addListener('disconnected', setDisconnected)
    ua.addListener('newRTCSession', onNewRTCSession)
    ua.addListener('registrationExpiring', register)
  }

  const unregister = () => {
    if (!ua) return
    ua.unregister({
      all: true,
    })
  }

  const subscribe = (internalNumber: string): Subscriber => {
    if (!confUri) throw new Error(`uaConf is ${confUri}`)
    const ownInternalNumber = confUri.user.replace('$ROOT', '')
    const fromUri = new URI('sip', ownInternalNumber, confUri.host, confUri.port)

    const toUri = new URI('sip', internalNumber, fromUri.host, fromUri.port)
    const subscriber = ua.subscribe(
      `sip:${internalNumber}`,
      'presence',
      'application/dialog-info+xml', {
        expires: SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS,
        params: {
          from_uri: fromUri,
          to_uri: toUri,
        },
      })

    subscriber.subscribe()

    return subscriber
  }

  return {
    init,
    start,
    stop,
    register,
    unregister,
    subscribe,
    registered: readonly(registered),
    registrationId: readonly(registrationId),
    call,
    getCurrentSession,
    activeSessionId: readonly(activeSessionId),
    removeSession,
    getRTCSessionFacadeById,
    getReferRTCSessionFacade,
    setActiveSession,
    sessions,
    ringingSessions,
    currentRTCSessionFacade,
    linkRoomToCallSessions,
    updateRoomInSessionIfNeed,
  }
}
